import {
  Alert,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Slide,
  Snackbar,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { fetchViaPost } from "../helpers/fetchViaPost";
import { forwardRef, useEffect, useRef, useState } from "react";
import { AppBar } from "../styled-components/AppBar";
import { TeamStrengthEditor } from "./TeamStrengthEditor";
import { LeagueDisplay } from "./LeagueDisplay";
import { fixInconsistencies } from "../helpers/fixInconsistencies";
import { checkForInconsistencies } from "../helpers/checkForInconsistencies";
import { ConfirmationDialog } from "./ConfirmationDialog";
import { useConfirm } from "material-ui-confirm";
import { fetchFromApi } from "../helpers/fetchFromApi";
import { ConfirmationDialogRaw } from "./ConfirmationDialogRaw";

const CustomAlert = forwardRef(function CustomAlert(props, ref) {
  return <Alert elevation={6} ref={ref} variant="filled" {...props} />;
});

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export const canRenderTeamStrengthEditor = (league, teams) =>
  Boolean(league?.current_season_id && Array.isArray(teams) && teams.length);

export function TeamStrengthDialog({ league, setOpen, user }) {
  const promptToSaveChanges = useConfirm();
  const handleUnsavedChanges = async () => {
    try {
      await promptToSaveChanges({
        title: `Unsaved changes detected`,
        description: "Would you like to save them?",
      });
      await saveUnsavedChanges();
    } catch {
      discardUnsavedChanges();
    }
  };

  const apiRef = useRef(null);
  useState(false);

  function handleClose() {
    setErrorMessage("");
    if (!hasChanges) setOpen(false);
    else handleUnsavedChanges();
  }

  async function setDefaultValues() {
    console.log("setDefaultValues", league.current_season_id, league.id);

    let jsonKey = JSON.stringify({
      season_id: league.current_season_id,
      league_id: league.id,
    });

    const resp = await (
      await fetchViaPost(
        "create-season-default",
        JSON.stringify({
          season_id: league.current_season_id,
          league_id: league.id,
          league_name: league.name,
        }),
      )
    ).json();
    if (resp) {
      setSeasonInitNeeded(false);
      setInconsistenciesFound(false);
      //handleClose();
      fetchTeams();
    } else {
      setErrorMessage(
        "Last season cannot be used! Please try another initialization type!",
      );
    }
  }

  async function duplicateValuesFromCreatedSeason() {
    let jsonKey = JSON.stringify({
      season_id: league.current_season_id,
      league_id: league.id,
    });

    const resp = await (
      await fetchViaPost(
        "duplicate-from-created",
        JSON.stringify({
          season_id: league.current_season_id,
          league_id: league.id,
          league_name: league.name,
        }),
      )
    ).json();
    if (resp) {
      setSeasonInitNeeded(false);
      setInconsistenciesFound(false);
      await fetchTeams();
    } else {
      setErrorMessage('Season is not initialized! Please contact "wettopa"!');
    }
  }

  async function duplicateValuesFromPreviousSeason() {
    console.log(
      "duplicateValuesFromPreviousSeason",
      league
    );

    try {
      const result = await (
        await fetchViaPost(
          "duplicate-season-as-previous",
          JSON.stringify({
            season_id:
              league.current_season_id,
            league_id:
              league.id,
          }),
        )
      ).json();

      if (!result?.success) {
        throw new Error(
          result?.message ||
          "Take from last season failed."
        );
      }

      setSeasonInitNeeded(false);
      setInconsistenciesFound(false);

      /*
       * Falls Teams neu in der Liga sind,
       * halten wir hier bewusst an.
       *
       * Die gebliebenen Teams wurden bereits
       * aus der Vorsaison übernommen.
       * Die neuen Teams werden jetzt vom
       * Benutzer kontrolliert.
       */
      if (
        result.requires_transition_confirmation &&
        Array.isArray(result.transitions) &&
        result.transitions.length > 0
      ) {
        setTransitionInitialization({
          ...result,

          transitions:
            result.transitions.map(
              (transition) => ({
                ...transition,

                total_strength:
                  Number(
                    transition
                      .recommended_strength
                  ),
              })
            ),
        });

        return;
      }

      /*
       * Keine neuen Teams:
       * Initialisierung ist bereits vollständig.
       */
      await new Promise(
        (resolve) =>
          setTimeout(resolve, 500)
      );

      await fetchTeams();

    } catch (e) {
      setErrorMessage(
        e?.message ||
        "Last season cannot be used! Please try another initialization type!",
      );
    }
  }

  function updateTransitionStrength(
    teamId,
    value
  ) {
    setTransitionInitialization(
      (current) => {
        if (!current) {
          return current;
        }

        return {
          ...current,

          transitions:
            current.transitions.map(
              (transition) =>
                Number(
                  transition.team_id
                ) === Number(teamId)
                  ? {
                      ...transition,

                      total_strength:
                        value,
                    }
                  : transition
            ),
        };
      }
    );
  }


  async function applyTransitionInitialization() {
    if (!transitionInitialization) {
      return;
    }

    const transitions =
      transitionInitialization.transitions.map(
        (transition) => ({
          ...transition,

          total_strength:
            Number(
              transition.total_strength
            ),
        })
      );

    const invalid =
      transitions.find(
        (transition) =>
          !Number.isFinite(
            transition.total_strength
          ) ||
          transition.total_strength <= 0
      );

    if (invalid) {
      setErrorMessage(
        `Ungültiger Wert für ${
          invalid.team_name ||
          invalid.team_id
        }.`
      );

      return;
    }

    setApplyingTransitions(true);

    try {
      const result = await (
        await fetchViaPost(
          "duplicate-season-as-previous",
          JSON.stringify({
            mode:
              "apply_transitions",

            league_id:
              transitionInitialization
                .league_id,

            season_id:
              transitionInitialization
                .season_id,

            transitions,
          }),
        )
      ).json();

      if (!result?.success) {
        throw new Error(
          result?.message ||
          "Neue Teams konnten nicht initialisiert werden."
        );
      }

      setTransitionInitialization(null);

      await new Promise(
        (resolve) =>
          setTimeout(resolve, 500)
      );

      await fetchTeams();

    } catch (error) {
      setErrorMessage(
        error?.message ||
        String(error)
      );
    } finally {
      setApplyingTransitions(false);
    }
  }


  async function saveUnsavedChanges() {
    await handleSave();
    setOpen(false);
    setHasChanges(false);
  }

  function discardUnsavedChanges() {
    setOpen(false);
    setHasChanges(false);
  }

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [
    autoInitializedTeams,
    setAutoInitializedTeams,
  ] = useState([]);

  const [
    transitionInitialization,
    setTransitionInitialization,
  ] = useState(null);

  const [
    applyingTransitions,
    setApplyingTransitions,
  ] = useState(false);

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setSnackbarOpen(false);
  };

  async function handleSave() {
    const strengths = rows.map((e) => {
      const { rank, total_goals, custom_name, ...row } = e;
      return row;
    });
    await fetchViaPost("put-team-strengths", JSON.stringify(strengths));

    /*
     * put-team-strengths aktualisiert auch die aus den
     * Team Settings abgeleiteten Season Parameters.
     * Deshalb muss der Editor diese Werte nach dem Save
     * erneut vom Backend lesen.
     */
    setSeasonParamsReloadKey((value) => value + 1);

    setSnackbarOpen(true);
    setHasChanges(false);
  }

  async function fetchTeams() {
    setTeams([]);

    var season = await (
      await fetchFromApi(
        "fetch-data-v3/leagues/" + league.id + "?per_page=50&include=seasons",
      )
    ).json();
    league.current_season_id = season.data.seasons.find(
      (s) => s.is_current === true,
    ).id;

    console.log("LL", league.current_season_id, league.id);

    try {
      const result = await (
        await fetchViaPost(
          "get-team-strengths-by-season-id",
          JSON.stringify({
            season_id: league.current_season_id,
            league_id: league.id,
          }),
        )
      ).json();

      console.log(
        "Fetched teams:",
        result
      );

      const teamMap =
        result?.strengths ?? result ?? {};

      const values =
        Object.values(teamMap);

      setSeasonInitNeeded(
        Object.keys(teamMap).length === 0 &&
        teamMap.constructor === Object,
      );

      setTeams(values);

      if (
        !checkForInconsistencies(values)
      ) {
        setInconsistenciesFound(true);
      }

      const newTeams =
        Array.isArray(
          result?.auto_initialized_teams
        )
          ? result.auto_initialized_teams
          : [];

      if (newTeams.length > 0) {
        setAutoInitializedTeams(
          newTeams
        );
      }
    } catch (ex) {
      setSeasonInitNeeded(true);
    }
  }

  const [teams, setTeams] = useState([]);
  const [rows, setRows] = useState([]);
  const [inconsistenciesFound, setInconsistenciesFound] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [closeParent, setCloseParent] = useState(false);

  const [seasonInitNeeded, setSeasonInitNeeded] = useState(false);
  const [newValue, setNewValue] = useState(false);

  /*
   * Wird nach erfolgreichem Save erhöht.
   * Der TeamStrengthEditor verwendet diesen Wert,
   * um die gespeicherten Season Parameters neu zu laden.
   */
  const [seasonParamsReloadKey, setSeasonParamsReloadKey] = useState(0);

  useEffect(() => {
    if (!league) return;

    fetchTeams();
  }, [league]);

  return (
    <div>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <CustomAlert
          onClose={handleSnackbarClose}
          severity="success"
          sx={{ width: "100%" }}
        >
          Your changes have been saved.
        </CustomAlert>
      </Snackbar>
      <Dialog
        open={
          autoInitializedTeams.length > 0
        }
        onClose={() =>
          setAutoInitializedTeams([])
        }
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Team Settings automatisch ergänzt
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography>
              Sportmonks meldet für diesen
              Wettbewerb neue Teilnehmer.
              Für folgende Teams wurden
              deshalb neutrale Team Settings
              angelegt:
            </Typography>

            {autoInitializedTeams.map(
              (team) => (
                <Typography
                  key={team.team_id}
                  sx={{ fontWeight: 600 }}
                >
                  {team.team_name ||
                    `Team ${team.team_id}`}
                </Typography>
              )
            )}

            <Alert severity="info">
              Die neuen Teams wurden zunächst
              mit neutralen Standardwerten
              initialisiert. Bitte die
              Spielstärken prüfen und bei
              Bedarf nachjustieren.
            </Alert>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            variant="contained"
            onClick={() =>
              setAutoInitializedTeams([])
            }
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationDialogRaw
        user={user}
        errorMessage={errorMessage}
        open={seasonInitNeeded}
        onCancel={(value) => {
          setInconsistenciesFound(false);
          setSeasonInitNeeded(false);
          handleClose();
        }}
        onConfirm={(value) => {
          if (typeof value === "string" && value === "Take neutral values") {
            setDefaultValues();
          }
          if (typeof value === "string" && value === "Take from last season") {
            duplicateValuesFromPreviousSeason();
          }
          if (typeof value === "string" && value === 'Take from "wettopa"') {
            duplicateValuesFromCreatedSeason();
          }
          if (typeof value === "string" && value === "Guided initialization") {
            setInconsistenciesFound(false);
            setSeasonInitNeeded(false);
            handleClose();
          }
        }}
      />
      <ConfirmationDialog
        open={inconsistenciesFound}
        onAbort={() => {
          setInconsistenciesFound(false);
        }}
        onConfirm={() => {
          setTeams(fixInconsistencies(teams));
          setInconsistenciesFound(false);
        }}
        title="Inconsistencies found"
        description="Would you like to fix them (recommended)?"
      />
      <Dialog
        open={Boolean(
          transitionInitialization
        )}
        fullWidth
        maxWidth="md"
        disableEscapeKeyDown
      >
        <DialogTitle>
          Neue Teams erkannt
        </DialogTitle>

        <DialogContent dividers>
          {transitionInitialization && (
            <Stack spacing={2}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
              >
                <LeagueDisplay
                  league={league}
                  compact
                />
                <Typography>
                  {" – "}
                  {transitionInitialization
                    .season_name}
                </Typography>
              </Stack>

              <Typography>
                {
                  transitionInitialization
                    .retained_team_count
                } Teams wurden aus der
                Vorsaison übernommen.{" "}
                {
                  transitionInitialization
                    .removed_team_count
                } Teams sind nicht mehr in
                der Liga.{" "}
                {
                  transitionInitialization
                    .new_team_count
                } Teams sind neu.
              </Typography>

              <Alert severity="info">
                Neue Teams werden gesondert
                initialisiert. Als erster
                Standardwert verwendet betty
                0,80 für Aufsteiger und 1,15
                für Absteiger. Die Werte
                können hier vor der
                Initialisierung geändert
                werden.
              </Alert>

              {transitionInitialization
                .transitions
                .map((transition) => {
                  const isRelegated =
                    transition
                      .transition_type ===
                    "relegated";

                  return (
                    <Stack
                      key={
                        transition.team_id
                      }
                      spacing={1}
                      sx={{
                        p: 2,
                        border: 1,
                        borderColor:
                          "divider",
                        borderRadius: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                        }}
                      >
                        {transition
                          .team_name ||
                          `Team ${transition.team_id}`}
                      </Typography>

                      <Typography>
                        <strong>
                          Einordnung:
                        </strong>{" "}
                        {isRelegated
                          ? "Absteiger"
                          : "Aufsteiger"}
                      </Typography>

                      <Typography>
                        <strong>
                          Herkunft:
                        </strong>{" "}
                        {transition
                          .source_league_name ||
                          "nicht eindeutig ermittelt"}
                        {transition
                          .source_season_name
                          ? ` – ${transition.source_season_name}`
                          : ""}
                      </Typography>

                      {Number.isFinite(
                        Number(
                          transition
                            .source_position
                        )
                      ) && (
                        <Typography>
                          <strong>
                            Platzierung:
                          </strong>{" "}
                          {
                            transition
                              .source_position
                          }
                        </Typography>
                      )}

                      <TextField
                        label="Initiale Spielstärke"
                        type="number"
                        value={
                          transition
                            .total_strength
                        }
                        onChange={(
                          event
                        ) =>
                          updateTransitionStrength(
                            transition.team_id,
                            event.target.value
                          )
                        }
                        inputProps={{
                          step: "0.01",
                        }}
                        fullWidth
                      />
                    </Stack>
                  );
                })}
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            variant="contained"
            disabled={
              applyingTransitions
            }
            onClick={
              applyTransitionInitialization
            }
          >
            {applyingTransitions
              ? "Initialisierung läuft …"
              : "Neue Teams initialisieren"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        fullScreen
        open={Boolean(league)}
        onClose={handleClose}
        TransitionComponent={Transition}
      >
        <AppBar sx={{ position: "relative" }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleClose}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              <LeagueDisplay league={league} compact />
            </Typography>
            <Button
              autoFocus
              color="inherit"
              onClick={handleSave}
              title="Saving your changes You can´t restore your former settings. You can still manually adjust again at any point."
            >
              save
            </Button>
          </Toolbar>
        </AppBar>
        <Container maxWidth="xxl">
          {!canRenderTeamStrengthEditor(league, teams) ? (
            <CircularProgress />
          ) : (
            <TeamStrengthEditor
              user_id={user.id}
              season_id={league.current_season_id}
              teams={teams}
              rows={rows}
              setRows={setRows}
              apiRef={apiRef}
              setHasChanges={setHasChanges}
              setCloseParent={handleClose}
              seasonParamsReloadKey={seasonParamsReloadKey}
            />
          )}
        </Container>
      </Dialog>
    </div>
  );
}
